import React, { useState, useRef, useEffect, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { FaCrosshairs } from "react-icons/fa";
import { Button, Modal } from 'antd';

// Set Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOXGL_ACCESS_TOKEN;

const GoogleMapLocationPicker = ({ location, setLocation }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const addMarker = useCallback((loc) => {
    if (!mapRef.current) return;

    if (markerRef.current) {
      markerRef.current.remove();
    }

    markerRef.current = new mapboxgl.Marker({ color: "#2563EB" })
      .setLngLat([loc.lng, loc.lat])
      .addTo(mapRef.current);
  }, []);

  // Initialize map when modal opens
  useEffect(() => {
    if (!isModalOpen) {
      // Cleanup when modal closes
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        setMapLoaded(false);
      }
      return;
    }

    // Wait for DOM to be ready
    const initMap = () => {
      if (!mapContainerRef.current || mapRef.current) return;

      const initialCenter = location || mapCenter;

      try {
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: "mapbox://styles/mapbox/streets-v12",
          center: [initialCenter.lng, initialCenter.lat],
          zoom: 13,
        });

        mapRef.current.on('load', () => {
          setMapLoaded(true);

          // Add navigation controls
          mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");

          // Add click handler for picking location
          mapRef.current.on("click", (e) => {
            const newLoc = {
              lat: e.lngLat.lat,
              lng: e.lngLat.lng,
            };
            setSelectedLocation(newLoc);
            setMapCenter(newLoc);

            if (markerRef.current) {
              markerRef.current.remove();
            }
            markerRef.current = new mapboxgl.Marker({ color: "#2563EB" })
              .setLngLat([newLoc.lng, newLoc.lat])
              .addTo(mapRef.current);
          });

          // Add initial marker if location exists
          if (location) {
            setSelectedLocation(location);
            markerRef.current = new mapboxgl.Marker({ color: "#2563EB" })
              .setLngLat([location.lng, location.lat])
              .addTo(mapRef.current);
          }
        });
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    };

    // Use requestAnimationFrame to ensure DOM is painted
    const timer = setTimeout(() => {
      requestAnimationFrame(initMap);
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, [isModalOpen, location]);

  // Get current location
  const getCurrentLocation = () => {
    setIsLoading(true);
    setErrorMsg("");

    if (!navigator.geolocation) {
      setErrorMsg("Geolocation is not supported by your browser");
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newLocation = { lat: latitude, lng: longitude };

        setSelectedLocation(newLocation);
        setMapCenter(newLocation);

        if (mapRef.current) {
          if (markerRef.current) {
            markerRef.current.remove();
          }
          markerRef.current = new mapboxgl.Marker({ color: "#2563EB" })
            .setLngLat([newLocation.lng, newLocation.lat])
            .addTo(mapRef.current);

          mapRef.current.flyTo({
            center: [newLocation.lng, newLocation.lat],
            zoom: 15,
          });
        }

        setIsLoading(false);
      },
      () => {
        setErrorMsg("Unable to retrieve your location");
        setIsLoading(false);
      }
    );
  };

  // Search for places using Mapbox Geocoding API
  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (query.length < 3) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxgl.accessToken}&limit=5`
      );
      const data = await response.json();

      if (data.features) {
        setSearchResults(data.features);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  // Handle place selection from search
  const handlePlaceSelect = (place) => {
    const [lng, lat] = place.center;
    const newLocation = { lat, lng };

    setSelectedLocation(newLocation);
    setMapCenter(newLocation);
    setSearchQuery(place.place_name);
    setShowSuggestions(false);
    setSearchResults([]);

    if (mapRef.current) {
      if (markerRef.current) {
        markerRef.current.remove();
      }
      markerRef.current = new mapboxgl.Marker({ color: "#2563EB" })
        .setLngLat([lng, lat])
        .addTo(mapRef.current);

      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: 15,
      });
    }
  };

  const showModal = () => {
    setIsModalOpen(true);
    if (location) {
      setSelectedLocation(location);
      setMapCenter(location);
    }
  };

  const handleOk = () => {
    setLocation(selectedLocation);
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <div>
      <Button type="primary" onClick={showModal}>
        Select Location
      </Button>
      <Modal
        title="Select Location"
        open={isModalOpen}
        onOk={handleOk}
        okText={'Save'}
        maskClosable={false}
        closable={false}
        onCancel={handleCancel}
        width={600}
      >
        <div className="w-full mx-auto">
          {/* Header Controls */}
          <div className="mb-4 flex justify-between items-center">
            <button
              onClick={getCurrentLocation}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 transition-colors"
              disabled={isLoading}
            >
              <FaCrosshairs className="w-4 h-4" />
              {isLoading ? "Getting Location..." : "Current Location"}
            </button>
          </div>

          {/* Search Input with Suggestions */}
          <div className="mb-4 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Search a location..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((place) => (
                  <div
                    key={place.id}
                    onClick={() => handlePlaceSelect(place)}
                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <p className="text-sm font-medium text-gray-800">{place.text}</p>
                    <p className="text-xs text-gray-500">{place.place_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {errorMsg && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{errorMsg}</div>}

          {/* Map Container */}
          <div
            ref={mapContainerRef}
            style={{ width: "100%", height: "300px", borderRadius: "8px" }}
          />

          {/* Selected Location Info */}
          {selectedLocation && selectedLocation.lat != null && selectedLocation.lng != null && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-2">Selected Location</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Latitude</p>
                  <p className="font-mono">{selectedLocation.lat.toFixed(6)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Longitude</p>
                  <p className="font-mono">{selectedLocation.lng.toFixed(6)}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default GoogleMapLocationPicker;
